import {
  IPropertySearchInstanceDataFilters,
  IPropertySearchInstanceFields,
  IPropertySearchInstanceSorts,
  IPropertySearchInstanceUiFilters,
  IPropertySearchInstances,
} from '@llws/lift-entity-interfaces';
import { ISelectOption } from '../interfaces';
import { cloneObject } from './clone-object';
import { fpnCustomFieldTargetOptions } from './static-select-options';
import { TBuilderSidebarFieldMap } from '@/shared/types/t-builder-sidebar-map';

export type WithExtraFields<T> = T & { isOpen: boolean, order: number };

export type TPSBFields =
  | IPropertySearchInstanceFields
  | IPropertySearchInstanceSorts
  | IPropertySearchInstanceDataFilters
  | IPropertySearchInstanceUiFilters
  | IPropertySearchInstances;

export type TPSBFieldsArray = TPSBFields[];

export type TPSBPresetFields =
  | WithExtraFields<IPropertySearchInstanceFields>
  | WithExtraFields<IPropertySearchInstanceSorts>
  | WithExtraFields<IPropertySearchInstanceDataFilters>
  | WithExtraFields<IPropertySearchInstanceUiFilters>
  | WithExtraFields<IPropertySearchInstances>;

export type TPSBBuilderSettings = Partial<IPropertySearchInstances> & {
  displayOption?: string;
  sortLabel?: string;
  sortBy?: string;
  sortByOrder?: string;
  groupBy?: string | null;
  sortByCustomFieldKey?: string;
  groupByCustomFieldKey?: string;
  status?: string;
};

export interface IPSBFieldMap {
  hidden: string[];
  translatable: string[];
  inputTypes: Record<string, string>;
  options: Record<string, ISelectOption[]>;
  fields: Record<string, string | number | boolean | null | unknown[]>[];
  displayKey: string;
  helpText?: Record<string, string>;
  refKey: string;
}

export type TPSBPresetJSONFields = 'dataFilters' | 'uiFilters' | 'sorts' | 'fields' | 'instances';

export type TPSBTranslatable<T> = {
  [id: string]: T;
};

const filterFieldConfig: TBuilderSidebarFieldMap = {
  hidden: [
    'status',
    'psiId',
    'modifiedUserId',
    'order',
    'modified',
    'id',
    'createdUserId',
    'created',
    'createdAt',
    'clientId',
    'client',
  ],
  translatable: ['format', 'label'],
  displayKey: 'label',
  customLabel: (item) => {
    if (!item.tableColumnValues) {
      return item.tableColumn;
    }

    return `Show ${item.tableColumnValues}`;
  },
  refKey: 'tableColumn',
  inputTypes: {
    increments: 'number',
    customFieldKey: 'customField',
  },
  options: {
    customFieldTarget: [
      {
        text: 'Select Building or Unit Field',
        value: '',
      },
      ...fpnCustomFieldTargetOptions,
    ],
    operation: [
      {
        text: 'Select Option',
        value: '',
      },
      {
        text: 'Exact Match',
        value: 'equal',
      },
      {
        text: 'Min',
        value: 'min',
      },
      {
        text: 'Max',
        value: 'max',
      },
      {
        text: 'Between',
        value: 'between',
      },
      {
        text: 'Like',
        value: 'like',
      },
    ],
    inputType: [
      {
        text: 'Select Option',
        value: '',
      },
      {
        text: 'Dropdown',
        value: 'dropdown',
      },
      {
        text: 'Radio',
        value: 'radio',
      },
    ],
  },
  fields: [
    {
      label: 'Only residential',
      modelType: 'buildings',
      modelId: '',
      tableColumn: 'categoryTypes',
      tableColumnValues: 'residential',
    },
    {
      label: 'Only commercial',
      modelType: 'buildings',
      modelId: '',
      tableColumn: 'categoryTypes',
      tableColumnValues: 'commercial',
    },
    {
      label: 'By ownership type',
      modelType: 'buildings',
      modelId: '',
      tableColumn: 'ownershipTypes',
      tableColumnValues: 'rent,condo,own,lease,lease-to-own,rent-to-own,for-sale',
    },
    {
      label: 'By neighbourhood',
      modelType: 'buildings',
      modelId: '',
      tableColumn: 'neighbourhoods',
      tableColumnValues: '',
    },
    {
      label: 'By city',
      modelType: 'buildings',
      modelId: '',
      tableColumn: 'cities',
      tableColumnValues: '',
    },
    {
      label: 'By Region',
      modelType: 'buildings',
      modelId: '',
      tableColumn: 'regions',
      tableColumnValues: '',
    },
    {
      label: 'Featured only',
      modelType: 'buildings',
      modelId: '',
      tableColumn: 'featured',
      tableColumnValues: 'true',
    },
  ],
};

const uiFilterFieldConfig: TBuilderSidebarFieldMap = {
  hidden: [
    ...filterFieldConfig.hidden,
  ],
  translatable: ['format', 'label'],
  displayKey: 'label',
  refKey: 'field',
  inputTypes: {
    increments: 'number',
    customFieldKey: 'customField',
  },
  options: {
    customFieldTarget: [
      {
        text: 'Select Building or Unit Field',
        value: '',
      },
      ...fpnCustomFieldTargetOptions,
    ],
    operation: [
      {
        text: 'Select Option',
        value: '',
      },
      {
        text: 'Exact Match',
        value: 'equal',
      },
      {
        text: 'Min',
        value: 'min',
      },
      {
        text: 'Max',
        value: 'max',
      },
      {
        text: 'Between',
        value: 'between',
      },
      {
        text: 'Like',
        value: 'like',
      },
    ],
    inputType: [
      {
        text: 'Single Select',
        value: 'singleSelect',
      },
      {
        text: 'Range',
        value: 'range',
      },
      {
        text: 'Multi Select',
        value: 'multiSelect',
      },
    ],
    filterMatchType: [
      {
        text: 'Range',
        value: 'range',
      },
      {
        text: 'Min',
        value: 'min',
      },
      {
        text: 'Max',
        value: 'max',
      },
      {
        text: 'Exact',
        value: 'exact',
      },
    ],
    modelSource: [
      {
        text: 'Unit',
        value: 'unit',
      },
      {
        text: 'Property',
        value: 'property',
      },
    ],
  },
  fields: [
    {
      modelSource: 'unit',
      field: 'beds',
      filterMatchType: 'exact',
      inputType: 'singleSelect',
      format: '[[ value ]]',
      label: 'Bedrooms',
      showAllLabel: '',
      increments: 1,
    },
    {
      modelSource: 'unit',
      field: 'bath',
      filterMatchType: 'exact',
      inputType: 'singleSelect',
      format: '[[ value ]]',
      label: 'Bathrooms',
      showAllLabel: 'Any',
      increments: 1,
    },
    {
      modelSource: 'unit',
      field: 'den',
      filterMatchType: 'exact',
      inputType: '',
      format: '[[ value ]]',
      label: 'Den',
      showAllLabel: '',
    },
    {
      modelSource: 'unit',
      field: 'bedDen',
      filterMatchType: 'exact',
      inputType: 'singleSelect',
      format: '[[ value ]]',
      label: 'Bed + Den',
      showAllLabel: 'Any',
      increments: 1,
    },
    {
      field: 'sqFt',
      filterMatchType: 'exact',
      inputType: 'singleSelect',
      format: '[[ value ]]',
      label: 'SqFt',
      showAllLabel: 'Any',
      increments: 100,
    },
    {
      modelSource: 'property',
      field: 'propertyType',
      filterMatchType: 'exact',
      inputType: '',
      format: '[[ value ]]',
      label: 'Property Type',
      showAllLabel: '',
    },
    {
      modelSource: 'property',
      field: 'cities',
      filterMatchType: 'exact',
      inputType: 'singleSelect',
      format: '[[ value ]]',
      label: 'City',
      showAllLabel: '',
    },
    {
      modelSource: 'property',
      field: 'provinces',
      filterMatchType: 'exact',
      inputType: 'singleSelect',
      format: '[[ value ]]',
      label: 'Province',
      showAllLabel: '',
    },
    {
      modelSource: 'property',
      field: 'countries',
      filterMatchType: 'exact',
      inputType: 'singleSelect',
      format: '[[ value ]]',
      label: 'Country',
      showAllLabel: '',
    },
    // `field` is the filter key sent to the web-components-api search, so it must match a key the API reads
    // (amenities/utilities/pets only exist as `*And`/`*Or`; options still load from the base name).
    // Known API-side gaps, pre-dating WEBSITES-652:
    // - `provinces`, `countries`: options load, but the search API has no filter for them.
    // - `unitType`: filters, but no options load (dynamic ranges only build them for `unitTypes`).
    // - `petsAnd`: looks up `pets` options, but the API returns them as `petFriendlyBuildings`.
    // - `regions`, `intersections`, `categoryTypes` and `propertyTypeGroups` have no options source.
    {
      modelSource: 'property',
      field: 'regions',
      filterMatchType: 'exact',
      inputType: 'singleSelect',
      format: '[[ value ]]',
      label: 'Region',
      showAllLabel: '',
    },
    {
      modelSource: 'property',
      field: 'neighbourhoods',
      filterMatchType: 'exact',
      inputType: 'singleSelect',
      format: '[[ value ]]',
      label: 'Neighbourhood',
      showAllLabel: '',
    },
    {
      modelSource: 'property',
      field: 'intersections',
      filterMatchType: 'exact',
      inputType: 'singleSelect',
      format: '[[ value ]]',
      label: 'Intersection',
      showAllLabel: '',
    },
    {
      modelSource: 'property',
      field: 'buildingTypes',
      filterMatchType: 'exact',
      inputType: 'multiSelect',
      format: '[[ value ]]',
      label: 'Building Type',
      showAllLabel: '',
    },
    {
      modelSource: 'property',
      field: 'categoryTypes',
      filterMatchType: 'exact',
      inputType: 'multiSelect',
      format: '[[ value ]]',
      label: 'Category Type',
      showAllLabel: '',
    },
    {
      modelSource: 'property',
      field: 'propertyTypeGroups',
      filterMatchType: 'exact',
      inputType: 'multiSelect',
      format: '[[ value ]]',
      label: 'Property Type Group',
      showAllLabel: '',
    },
    {
      modelSource: 'property',
      field: 'ownershipTypes',
      filterMatchType: 'exact',
      inputType: 'multiSelect',
      format: '[[ value ]]',
      label: 'Ownership Type',
      showAllLabel: '',
    },
    {
      modelSource: 'property',
      field: 'housingTypes',
      filterMatchType: 'exact',
      inputType: 'multiSelect',
      format: '[[ value ]]',
      label: 'Housing Type',
      showAllLabel: '',
    },
    {
      modelSource: 'property',
      field: 'petsAnd',
      filterMatchType: 'exact',
      inputType: 'multiSelect',
      format: '[[ value ]]',
      label: 'Pets',
      showAllLabel: '',
    },
    {
      modelSource: 'property',
      field: 'featured',
      filterMatchType: 'exact',
      inputType: '',
      format: '[[ value ]]',
      label: 'Featured',
      showAllLabel: '',
    },
    {
      modelSource: 'property',
      field: 'amenitiesAnd',
      filterMatchType: 'exact',
      inputType: 'multiSelect',
      format: '[[ value ]]',
      label: 'Amenities',
      showAllLabel: '',
    },
    {
      modelSource: 'property',
      field: 'utilitiesAnd',
      filterMatchType: 'exact',
      inputType: 'multiSelect',
      format: '[[ value ]]',
      label: 'Utilities',
      showAllLabel: '',
    },
    {
      modelSource: 'unit',
      field: 'unitType',
      filterMatchType: 'exact',
      inputType: 'multiSelect',
      format: '[[ value ]]',
      label: 'Unit Type',
      showAllLabel: '',
    },
    {
      modelSource: 'unit',
      field: 'availability',
      filterMatchType: 'exact',
      format: '[[ value ]]',
      label: 'Availability',
      showAllLabel: '',
    },
    {
      modelSource: 'unit',
      field: 'availabilityDate',
      filterMatchType: 'exact',
      inputType: 'dropdown',
      format: 'Next [[ value ]] Months',
      label: 'Availability Date',
      showAllLabel: '',
    },
    {
      modelSource: 'unit',
      field: 'rate',
      filterMatchType: 'exact',
      inputType: '',
      format: '$[[ value ]]',
      increments: 0,
      label: 'Rate',
      showAllLabel: '',
    },
    {
      modelSource: 'property',
      field: 'promotions',
      filterMatchType: 'exact',
      format: '[[ value ]]',
      label: 'Promotions',
      showAllLabel: '',
    },
    {
      modelSource: 'unit',
      field: 'customField',
      filterMatchType: 'exact',
      inputType: '',
      format: '[[ value ]]',
      label: 'Custom Field',
      customFieldKey: '',
      customFieldTarget: '',
      showAllLabel: '',
    },
  ],
};

const sortFieldConfig: TBuilderSidebarFieldMap = {
  hidden: [
    'field',
    'status',
    'psiId',
    'psid',
    'psId',
    'modifiedUserId',
    'modified_user_id',
    'order',
    'modified',
    'id',
    'createdUserId',
    'created_user_id',
    'created',
    'createdAt',
    'clientId',
    'client',
  ],
  translatable: ['label'],
  displayKey: 'label',
  refKey: 'field',
  inputTypes: {
    customFieldKey: 'customField',
  },
  options: {
    customFieldTarget: [
      ...fpnCustomFieldTargetOptions,
    ],
    direction: [
      {
        text: 'Select a direction',
        value: '',
      },
      {
        text: 'Ascending',
        value: 'asc',
      },
      {
        text: 'Descending',
        value: 'desc',
      },
    ],
  },
  endlessFields: [
    'unit:bedMin',
    'unit:bathMin',
    'unit:sqFtMin',
    'property:buildingName',
    'unit:available',
    'unit:rateMin',
  ],
  fields: [
    {
      field: 'property:buildingName',
      label: 'Building Name',
      direction: 'asc',
    },
    {
      field: 'unit:bedMin',
      label: 'Bed',
      direction: 'asc',
    },
    {
      field: 'unit:bathMin',
      label: 'Bath',
      direction: 'asc',
    },
    {
      field: 'unit:sqFtMin',
      label: 'SqFt',
      direction: 'asc',
    },
    {
      field: 'unit:available',
      label: 'Availability',
      direction: 'asc',
    },
    {
      field: 'unit:rateMin',
      label: 'Rate',
      direction: 'asc',
    },
    {
      field: 'property:promotions',
      label: 'Promotions',
      direction: 'asc',
    },
  ],
};

const fieldFieldConfig: TBuilderSidebarFieldMap = {
  hidden: [
    'field',
    'status',
    'psiId',
    'psid',
    'psId',
    'modifiedUserId',
    'modified_user_id',
    'order',
    'modified',
    'id',
    'createdUserId',
    'created_user_id',
    'created',
    'createdAt',
    'clientId',
    'client',
  ],
  translatable: ['format', 'heading'],
  inputTypes: {
    sortable: 'toggle',
    customFieldKey: 'customField',
    settings: 'checkbox',
  },
  options: {
    valueFormat: [
      {
        text: 'None',
        value: 'none',
      },
      {
        text: 'Currency',
        value: 'currency',
      },
    ],
    style: [
      {
        text: 'Select a style',
        value: '',
      },
      {
        text: 'Title',
        value: 'title',
      },
      {
        text: 'Heading',
        value: 'heading',
      },
      {
        text: 'Text',
        value: 'text',
      },
    ],
    fields: [
      {
        text: 'Video',
        value: 'video',
      },
      {
        text: 'Virtual Tour',
        value: 'virtualTour',
      },
      {
        text: 'Photos',
        value: 'photos',
      },
      {
        text: 'Floorplan',
        value: 'floorplan',
      },
    ],
    customFieldTarget: [
      ...fpnCustomFieldTargetOptions,
    ],
    settings: [
      // {
      //   text: 'Video',
      //   value: 'videos',
      // },
      // {
      //   text: 'Virtual Tour',
      //   value: 'virtualTours',
      // },
      {
        text: 'Photos',
        value: 'photos',
      },
      // {
      //   text: 'Floorplan',
      //   value: 'floorplans',
      // },
    ],
  },
  displayKey: 'field',
  refKey: 'field',
  fields: [
    {
      field: 'carousel',
      heading: 'Media',
      sectionKey: '',
      settings: '',
    },
    {
      field: 'buildingName',
      heading: 'Building Name',
      format: '[[ value ]]',
      sectionKey: '',
      style: '',
    },
    {
      field: 'rate',
      heading: 'Starting From',
      format: '$[[ value ]]',
      sectionKey: '',
      style: '',
    },
    {
      field: 'unitsAvailable',
      heading: 'Units Available',
      format: '[[ value ]]',
      sectionKey: '',
      style: '',
    },
    {
      field: 'cta',
      heading: 'Call to Action',
      format: '[[ value ]]',
      sectionKey: '',
      style: '',
    },
    {
      field: 'bed',
      heading: 'Bedrooms',
      format: '[[ bed |> 0: Batchelor | 1: 1 Bedroom | : [[ value ]] Bedrooms ]] [[ den ? + Den ]]',
      sectionKey: '',
      style: '',
    },
    {
      field: 'bath',
      heading: 'Baths Available',
      format: 'No Bathrooms | [[ value ]] Bathroom | [[ value ]] Bathrooms',
      sectionKey: '',
      style: '',
    },
    {
      field: 'den',
      heading: 'Den',
      format: '[[ value ]]',
      sectionKey: '',
      style: '',
    },
    {
      field: 'sqFt',
      heading: 'SqFt',
      format: '[[ value ]]',
      sectionKey: '',
      style: '',
    },
    {
      field: 'moveInDate',
      heading: 'Move-In Date',
      format: '[[ value ]]',
      sectionKey: '',
      style: '',
    },
    {
      field: 'address',
      heading: 'Address',
      format: '[[ streetNumber ]] [[ streetName ]], [[ cityName ]], [[ provinceCode ]] [[ postal ]]',
      sectionKey: '',
      style: '',
    },
    {
      field: 'promotions',
      heading: 'Promotions',
      format: '[[ value ]]',
      sectionKey: '',
      style: '',
    },
    {
      field: 'featured',
      heading: 'Featured',
      format: '[[ value ]]',
      sectionKey: '',
      style: '',
    },
    {
      field: 'matchingUnitCount',
      heading: 'Matching Unit Count',
      format: '[[ available.available ]] units available',
      sectionKey: '',
      style: '',
    },
    {
      field: 'favourite',
      heading: 'Favourite',
      format: '[[ value ]]',
      sectionKey: '',
      style: '',
    },
    {
      field: 'customField',
      filterMatchType: 'exact',
      inputType: '',
      format: '[[ value ]]',
      label: 'Custom Field',
      customFieldKey: '',
      customFieldTarget: '',
      showAllLabel: '',
    },
  ],
};

const instanceGroupByOptions: ISelectOption[] = [
  { value: null, text: 'Ungrouped' },
  { value: 'typeName', text: 'Unit Name' },
  { value: 'rate', text: 'Rate' },
  { value: 'bed', text: 'Beds' },
  { value: 'bath', text: 'Baths' },
  { value: 'custom', text: 'Custom Fields' },
];

const instanceFieldConfig: TBuilderSidebarFieldMap = {
  hidden: ['id', 'clientId', 'mapSettingId', 'created', 'createdUserId', 'modified', 'modifiedUserId'],
  translatable: ['label'],
  displayKey: 'label',
  refKey: 'key',
  inputTypes: {
    resultCount: 'number',
  },
  options: {
    layout: [
      { text: 'Grid', value: 'grid' },
      { text: 'List', value: 'list' },
      { text: 'Map', value: 'map' },
    ],
    searchType: [
      { text: 'Property', value: 'property' },
      { text: 'Unit', value: 'unit' },
    ],
    defaultSort: [
      { text: 'Name (A-Z)', value: 'name_asc' },
      { text: 'Name (Z-A)', value: 'name_desc' },
      { text: 'Price (Low to High)', value: 'price_asc' },
      { text: 'Price (High to Low)', value: 'price_desc' },
    ],
    unitGroupBy: instanceGroupByOptions,
    mapEnabled: [
      { text: 'Disabled', value: 0 },
      { text: 'Enabled', value: 1 },
    ],
    searchMode: [
      { text: 'Explicit', value: 'explicit' },
      { text: 'Dynamic', value: 'dynamic' },
    ],
    status: [
      { text: 'Enabled', value: 'enabled' },
      { text: 'Disabled', value: 'disabled' },
    ],
  },
  fields: [
    {
      key: 'property-grid',
      label: 'Property Grid Search',
      layout: 'grid',
      searchType: 'property',
      defaultSort: 'name_asc',
      resultCount: 12,
      unitGroupBy: null,
      mapEnabled: 1,
      searchMode: 'explicit',
      status: 'enabled',
    },
    {
      key: 'unit-list',
      label: 'Unit List Search',
      layout: 'list',
      searchType: 'property',
      defaultSort: 'price_asc',
      resultCount: 25,
      unitGroupBy: null,
      mapEnabled: 0,
      searchMode: 'explicit',
      status: 'enabled',
    },
    {
      key: 'map-search',
      label: 'Map Search',
      layout: 'map',
      searchType: 'property',
      defaultSort: 'price_asc',
      resultCount: 50,
      unitGroupBy: null,
      mapEnabled: 1,
      searchMode: 'explicit',
      status: 'enabled',
    },
  ],
};

/**
 * This field map is the source of truth for configuring PSB fields. We use it to populate
 * fields in the builder sidebar map for a clean config-based implementation.
 */
export const psbFieldsByKey: Record<TPSBPresetJSONFields, TBuilderSidebarFieldMap> = {
  dataFilters: filterFieldConfig,
  uiFilters: uiFilterFieldConfig,
  sorts: sortFieldConfig,
  fields: fieldFieldConfig,
  instances: instanceFieldConfig,
};

export const defaultSettings: TPSBBuilderSettings = {
  mapSettingId: 0,
  label: 'Property Grid Search',
  searchType: null,
  searchMode: 'explicit',
  defaultSort: 'b.buildingName_ASC',
  resultCount: '12',
  unitGroupBy: null,
  mapEnabled: 0,
  status: 'enabled',
};

/**
 * Returns a safe default PSB field map for the requested preset key.
 */
export const getDefaultPropertySearchFields = (
  key: TPSBPresetJSONFields,
  preset: Record<TPSBPresetJSONFields, IPSBFieldMap> = psbFieldsByKey,
) => {
  if (preset && Object.keys(preset[key] ?? {}).length) {
    return cloneObject(preset[key] ?? { en: [] });
  }

  if (preset && Array.isArray(preset[key] ?? [])) {
    return { en: [] };
  }

  return { en: [] };
};

export const groupByOptions: ISelectOption[] = [
  ...instanceGroupByOptions,
];

export const tableOptions: ISelectOption[] = [
  { value: 'card', text: 'Card' },
  { value: 'stacked', text: 'Stacked' },
];

export const searchTypeOptions: ISelectOption[] = [
  { text: 'Select Search Type', value: null },
  { text: 'Property', value: 'property' },
  { text: 'Unit', value: 'unit' },
];

export const searchModeOptions: ISelectOption[] = [
  { text: 'Explicit', value: 'explicit' },
  { text: 'Instant', value: 'instant' },
];

export const defaultSortOptions: ISelectOption[] = [
  { text: 'Name (A-Z)', value: 'b.buildingName_ASC' },
  { text: 'Name (Z-A)', value: 'b.buildingName_DESC' },
  { text: 'Price (Low to High)', value: 'st.rateMin_ASC' },
  { text: 'Price (High to Low)', value: 'st.rateMin_DESC' },
  { text: 'Promotions (Low to High)', value: 'b.promotions_ASC' },
  { text: 'Promotions (High to Low)', value: 'b.promotions_DESC' },
];
